import {useCallback, useEffect, useRef} from 'react';
import {useLeftPanelSync} from '../../hooks';
import {useReportStore} from '../../useReportStore';

const DOUBLE_CTRL_WINDOW_MS = 300;

export default function useEditorChatShortcut() {
    const {leftPanelTab, aiView, setLeftPanelTab, setAiView} = useLeftPanelSync();
    const editorView = useReportStore((state) => state.editorView);
    const rteRef = useReportStore((state) => state.rteRef);
    const chatComposerFocused = useReportStore((state) => state.chatComposerFocused);
    const requestChatComposerFocus = useReportStore((state) => state.requestChatComposerFocus);
    const lastCtrlKeyUpAtRef = useRef(0);
    const ctrlChordActiveRef = useRef(false);

    const focusEditor = useCallback(() => {
        if (editorView && typeof editorView.focus === 'function') {
            editorView.focus();
            return;
        }

        const candidate = rteRef?.current || rteRef;
        if (candidate && typeof candidate.focus === 'function') {
            candidate.focus();
            return;
        }

        if (candidate?.commands && typeof candidate.commands.focus === 'function') {
            candidate.commands.focus();
        }
    }, [editorView, rteRef]);

    const handleDoubleCtrl = useCallback(() => {
        if (chatComposerFocused) {
            focusEditor();
            return;
        }

        if (leftPanelTab === 'aiAssistant' && aiView) {
            setAiView(null);
        }

        if (leftPanelTab !== 'chat') {
            setLeftPanelTab('chat');
        }

        requestChatComposerFocus();
    }, [aiView, chatComposerFocused, focusEditor, leftPanelTab, requestChatComposerFocus, setAiView, setLeftPanelTab]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Control') {
                return;
            }

            if (event.ctrlKey) {
                ctrlChordActiveRef.current = true;
            }
        };

        const handleKeyUp = (event) => {
            if (event.key !== 'Control') {
                return;
            }

            if (ctrlChordActiveRef.current) {
                ctrlChordActiveRef.current = false;
                lastCtrlKeyUpAtRef.current = 0;
                return;
            }

            const now = Date.now();
            if (now - lastCtrlKeyUpAtRef.current <= DOUBLE_CTRL_WINDOW_MS) {
                lastCtrlKeyUpAtRef.current = 0;
                handleDoubleCtrl();
                return;
            }

            lastCtrlKeyUpAtRef.current = now;
        };

        const resetShortcutState = () => {
            ctrlChordActiveRef.current = false;
            lastCtrlKeyUpAtRef.current = 0;
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', resetShortcutState);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', resetShortcutState);
        };
    }, [handleDoubleCtrl]);
}

